import { AwaitContainer, Button, DataGrid } from '@ibsys/kit';
import { ListChecksIcon } from 'lucide-react';
import { FC, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SmartSearchInput } from '@/features/smart-search';
import { cn } from '@/shared/lib';
import { ContentSearchPanel } from './ContentSearch/ContentSearchPanel';
import type { DialogListDataGridProps } from './DialogListDataGrid.types';
import { DialogListToolbar, isToolbarStartPlated } from './DialogListToolbar';
import { FiltersDescription } from './FiltersDescription';
import { MassActionsBar } from './MassActionsBar';

export const DialogListDataGrid: FC<DialogListDataGridProps> = ({
  gridRef,
  columns,
  sorting,
  onSortingChange,
  filters,
  onFiltersChange,
  totalCount,
  fetchPage,
  cacheVersion,
  gridKey,
  searchValue,
  onSearchChange,
  onSearch,
  searchMode,
  onModeChange,
  onClearSearch,
  predicateInfo,
  searchError,
  isSearchLoading,
  isLoading,
  activeRowId,
  onClickRow,
  initialScrollIndex,
  onScrollIndexChange,
  gridColumnProps,
  columnsPanelWidth,
  onColumnsPanelWidthChange,
  columnsPanelExpandedGroups,
  onColumnsPanelExpandedGroupsChange,
  activeSidePanel,
  onActiveSidePanelChange,
  isSidePanelPinned,
  onSidePanelPinnedChange,
  headerOrientation,
  onHeaderOrientationChange,
  headerHeight,
  onHeaderHeightChange,
  filtersDescription,
  filtersDescriptionActions,
  hasContentSearch,
  activeContentTab,
  contentSearchInputValue,
  onContentSearchInputChange,
  onContentSearchClear,
  onContentSearchTabChange,
  onContentSearchSubmit,
  phraseSearchRole,
  onPhraseSearchRoleChange,
  phraseSearchEmployeeIds,
  onPhraseSearchEmployeeIdsChange,
  commentAuthorIds,
  onCommentAuthorIdsChange,
  canOpenActions,
  canRunPipeline,
  canDeleteConversations,
  canDownloadTranscriptArchive,
  isMassActionsActive,
  isRowSelectionActive,
  onToggleMassActions,
  rowSelection,
  onRowSelectionChange,
  selectedCount,
  hasSelection,
  onRunPipeline,
  onDeleteConversations,
  isRunPipelineLocked,
  isDeleteLocked,
  isTranscriptArchiveLocked,
  isDeletePending,
  isPreparingSelection,
  onDownloadTranscriptArchive,
  isTranscriptArchivePending,
  transcriptArchiveProgress,
  actionsSlot,
  footerSlot,
  className,
  // --- пропы панели действий: пробрасываются в DialogListToolbar ---
  canReset,
  onResetFilters,
  canSaveLayer,
  layerName,
  onSaveLayer,
  canExitLayer,
  onExitLayer,
  layerBlock,
  onDownloadList,
  isDownloadPending,
  controlsContainer,
}) => {
  const { t } = useTranslation(['dialogs']);
  // Описание рисуется только при непустых частях — по ним и виден факт показа строки
  const isDescriptionShown = Boolean(filtersDescription?.length);

  // Блок управления остаётся частью секции списка — он управляет её состоянием, — но рисуется
  // там, куда его поместила раскладка: рядом с карточкой диалога колонке списка он не помещается
  const controls = (
    // Плотность блока помечена на нём самом: раскладке, вынесшей блок из колонки списка, по этой
    // метке видно, какой промежуток до списка держать — сам блок расстояния до списка не знает
    <div
      data-controls-dense={isDescriptionShown || undefined}
      className={cn('flex flex-col', isDescriptionShown ? 'gap-2' : 'gap-4')}
    >
      <FiltersDescription parts={filtersDescription} actions={filtersDescriptionActions} />

      <ContentSearchPanel
        hasContentSearch={hasContentSearch}
        activeTab={activeContentTab}
        inputValue={contentSearchInputValue}
        onInputChange={onContentSearchInputChange}
        onClearInput={onContentSearchClear}
        onTabChange={onContentSearchTabChange}
        onSubmit={onContentSearchSubmit}
        phraseSearchRole={phraseSearchRole}
        onPhraseSearchRoleChange={onPhraseSearchRoleChange}
        phraseSearchEmployeeIds={phraseSearchEmployeeIds}
        onPhraseSearchEmployeeIdsChange={onPhraseSearchEmployeeIdsChange}
        commentAuthorIds={commentAuthorIds}
        onCommentAuthorIdsChange={onCommentAuthorIdsChange}
        leadingSlot={
          // Блок раздела занимает место переключателя «Действия»: раздел, служащий отбору
          // диалогов, показывает здесь свой выбор записей, а массовых действий не имеет.
          actionsSlot ??
          (canOpenActions ? (
            <Button
              variant={isMassActionsActive ? 'secondary' : 'ghost'}
              size="sm"
              aria-pressed={isMassActionsActive}
              onClick={onToggleMassActions}
              icon={<ListChecksIcon className="size-4" />}
            >
              {t('dialogs:dialogListSection.massActions.toggle')}
            </Button>
          ) : undefined)
        }
        leadingExpandedSlot={
          isMassActionsActive ? (
            <MassActionsBar
              selectedCount={selectedCount}
              hasSelection={hasSelection}
              isPreparingSelection={isPreparingSelection}
              canRunPipeline={canRunPipeline}
              isRunPipelineLocked={isRunPipelineLocked}
              onRunPipeline={onRunPipeline}
              canDeleteConversations={canDeleteConversations}
              isDeleteLocked={isDeleteLocked}
              isDeletePending={isDeletePending}
              onDeleteConversations={onDeleteConversations}
              canDownloadTranscriptArchive={canDownloadTranscriptArchive}
              isTranscriptArchiveLocked={isTranscriptArchiveLocked}
              isTranscriptArchivePending={isTranscriptArchivePending}
              transcriptArchiveProgress={transcriptArchiveProgress}
              onDownloadTranscriptArchive={onDownloadTranscriptArchive}
            />
          ) : undefined
        }
        searchExpandedSlot={
          activeContentTab === 'attribute' ? (
            <SmartSearchInput
              value={searchValue}
              onChange={onSearchChange}
              onSearch={onSearch}
              mode={searchMode}
              onModeChange={onModeChange}
              onClear={onClearSearch}
              predicateInfo={predicateInfo}
              error={searchError}
              isLoading={isSearchLoading}
              placeholder={t('dialogs:attributeSearch.placeholder')}
              className="min-w-0"
            />
          ) : undefined
        }
        isTrailingStartPlated={isToolbarStartPlated({ canReset, canSaveLayer, canExitLayer, layerName })}
        trailingSlot={
          <DialogListToolbar
            canReset={canReset}
            onResetFilters={onResetFilters}
            canSaveLayer={canSaveLayer}
            layerName={layerName}
            onSaveLayer={onSaveLayer}
            canExitLayer={canExitLayer}
            onExitLayer={onExitLayer}
            layerBlock={layerBlock}
            onDownloadList={onDownloadList}
            isDownloadPending={isDownloadPending}
          />
        }
      />
    </div>
  );

  return (
    // Промежуток до таблицы, пока блок управления остался здесь: с описанием он один на все три
    // строки — описание не отделено от строки управления сильнее, чем та от таблицы. Без описания
    // промежуток прежний, поэтому вне бета-функции страница выглядит как до неё
    <div className={cn(className, 'flex flex-col h-full', isDescriptionShown ? 'gap-2' : 'gap-4')}>
      {controlsContainer ? createPortal(controls, controlsContainer) : controls}

      <Suspense fallback={<></>}>
        <div ref={gridRef} className="flex flex-1 min-h-0 relative">
          {/* AwaitContainer только накрывает содержимое спиннером, поэтому грид монтируем сами:
              смонтированный он сразу запрашивает первую страницу, а параметры запроса (видимые
              колонки) до инициализации состояния колонок ещё не готовы */}
          <AwaitContainer isLoading={isLoading} className="h-full flex w-full">
            {!isLoading && (
              <DataGrid
                key={gridKey}
                {...gridColumnProps}
                columns={columns}
                className={'w-full rounded-r-none border-r-[0px]'}
                dataSource={{
                  mode: 'lazy',
                  totalCount,
                  pageSize: 100,
                  fetchPage,
                  cacheVersion,
                }}
                getRowId={(row) => String(row.id)}
                sorting={sorting}
                onSortingChange={onSortingChange}
                filters={filters}
                onFiltersChange={onFiltersChange}
                {...(isRowSelectionActive && {
                  selectionMode: 'multi' as const,
                  rowSelection,
                  onRowSelectionChange,
                })}
                activeRowId={activeRowId ?? null}
                onRowClick={(row, ctx) => onClickRow(row, ctx.event.metaKey || ctx.event.ctrlKey)}
                enableColumnResizing
                enableColumnReordering
                enableColumnPinning
                enableColumnHiding
                highlightHoveredColumn
                // Оформление панели задано глобально в kitSettings; здесь — только состояние
                // конкретного экземпляра, глобальным оно быть не может
                columnsPanel={{
                  width: columnsPanelWidth,
                  onWidthChange: onColumnsPanelWidthChange,
                  expandedGroups: columnsPanelExpandedGroups,
                  onExpandedGroupsChange: onColumnsPanelExpandedGroupsChange,
                }}
                filtersPanel
                // Открытая панель и её закрепление живут в настройках таблицы: раскладку списка
                // пользователь настраивает один раз, и она переживает и уход в карточку диалога,
                // и перезагрузку страницы.
                activeSidePanel={activeSidePanel}
                onActiveSidePanelChange={onActiveSidePanelChange}
                sidePanelPinned={isSidePanelPinned}
                onSidePanelPinnedChange={onSidePanelPinnedChange}
                headerOrientation={headerOrientation}
                onHeaderOrientationChange={onHeaderOrientationChange}
                enableHeaderResizing
                headerHeight={headerHeight}
                onHeaderHeightChange={onHeaderHeightChange}
                initialScrollIndex={initialScrollIndex}
                onScrollIndexChange={onScrollIndexChange}
              />
            )}
          </AwaitContainer>
        </div>
      </Suspense>

      {footerSlot}
    </div>
  );
};
